import type { Health, PriceChangeDirection } from "../../types";
import { cn } from "../lib/cn";
import { createNumberFormatter, createRelativeTimeFormatter } from "../lib/intl";
import { ArrowDownRightIcon, ArrowUpRightIcon, MinusIcon } from "./icons";

const priceFormatter = createNumberFormatter("fa-IR", {
  maximumFractionDigits: 0,
});

const relativeFormatter = createRelativeTimeFormatter("fa-IR", { numeric: "auto" });

function formatPrice(value?: number): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  return priceFormatter.format(value);
}

function formatRelative(timestamp?: number): string {
  if (!timestamp) {
    return "بدون داده";
  }
  const diffMs = Date.now() - timestamp;
  const diffSeconds = Math.round(diffMs / 1000);
  if (Math.abs(diffSeconds) < 1) {
    return "لحظه‌ای";
  }
  if (Math.abs(diffSeconds) < 60) {
    return relativeFormatter.format(-diffSeconds, "second");
  }
  const diffMinutes = Math.round(diffSeconds / 60);
  if (Math.abs(diffMinutes) < 60) {
    return relativeFormatter.format(-diffMinutes, "minute");
  }
  const diffHours = Math.round(diffMinutes / 60);
  return relativeFormatter.format(-diffHours, "hour");
}

function getArrow(change: PriceChangeDirection) {
  let icon = <MinusIcon className="h-4 w-4 text-slate-400" aria-hidden />;
  let label = "قیمت بدون تغییر";

  if (change === "up") {
    icon = <ArrowUpRightIcon className="h-4 w-4 text-emerald-400" aria-hidden />;
    label = "قیمت در حال افزایش";
  } else if (change === "down") {
    icon = <ArrowDownRightIcon className="h-4 w-4 text-rose-400" aria-hidden />;
    label = "قیمت در حال کاهش";
  }

  return (
    <span className="flex items-center" aria-label={label}>
      {icon}
      <span className="sr-only">{label}</span>
    </span>
  );
}

function getHealthColor(status: Health): string {
  switch (status) {
    case "OK":
      return "bg-emerald-500";
    case "CONNECTING":
      return "bg-amber-400";
    case "TIMEOUT":
      return "bg-orange-400";
    default:
      return "bg-rose-500";
  }
}

function getHealthLabel(status: Health): string {
  switch (status) {
    case "OK":
      return "اتصال پایدار";
    case "CONNECTING":
      return "در حال اتصال";
    case "TIMEOUT":
      return "پاسخ‌گویی کند شد";
    default:
      return "قطع ارتباط";
  }
}

export interface PriceCardProps {
  testId?: string;
  title: string;
  logo?: string;
  logoWebp?: string;
  link?: string;
  price?: number;
  health: Health;
  change: PriceChangeDirection;
  updatedAt?: number;
  averageDiffPct?: number;
  isStale?: boolean;
  showStaleBadge?: boolean;
}

export function PriceCard({
  title,
  logo,
  logoWebp,
  link,
  price,
  health,
  change,
  updatedAt,
  testId,
  isStale,
  showStaleBadge,
}: PriceCardProps) {
  const baseClass = cn(
    "block rounded-xl border border-slate-800 bg-slate-900/60 p-6 shadow-lg shadow-slate-950/40 backdrop-blur",
    "transition-transform duration-200 hover:-translate-y-1 hover:border-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500",
  );

  const hasTimestamp = typeof updatedAt === "number" && updatedAt > 0;
  const isLoading = health === "CONNECTING" && !hasTimestamp;
  const isStaleWithTimestamp = Boolean(isStale && hasTimestamp);
  const showStaleIndicator = Boolean(showStaleBadge && isStaleWithTimestamp);
  const shouldShowArrow = hasTimestamp && (!isStaleWithTimestamp || !showStaleIndicator);

  const priceContent = isLoading ? (
    <span className="block h-9 w-28 animate-pulse rounded-lg bg-slate-700/70" aria-hidden />
  ) : hasTimestamp ? (
    formatPrice(price)
  ) : (
    "—"
  );

  const currencyContent = isLoading ? (
    <span className="mt-1 block h-4 w-12 animate-pulse rounded bg-slate-800/70" aria-hidden />
  ) : hasTimestamp ? (
    "تومان"
  ) : (
    "—"
  );

  const changeContent = isLoading ? (
    <span className="inline-flex items-center gap-2 text-xs text-slate-400" aria-live="polite">
      <span className="relative flex h-2 w-2" aria-hidden>
        <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-slate-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-slate-500" />
      </span>
      در حال دریافت قیمت
    </span>
  ) : shouldShowArrow ? (
    getArrow(change)
  ) : showStaleIndicator ? (
    <span className="text-xs text-amber-300">داده قدیمی</span>
  ) : (
    <span className="text-xs text-slate-400">داده‌ای دریافت نشد</span>
  );

  const staleBadge = showStaleIndicator ? (
    <span className="inline-flex items-center rounded-full border border-amber-400/60 bg-amber-400/10 px-2 py-0.5 text-[11px] font-medium text-amber-200">
      داده قدیمی
    </span>
  ) : null;

  const brandMark = (
    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-800 bg-slate-800/60">
      {logo ? (
        <picture>
          {logoWebp ? <source srcSet={logoWebp} type="image/webp" /> : null}
          <img
            src={logo}
            alt={`لوگوی صرافی ${title}`}
            loading="lazy"
            width={48}
            height={48}
            className="h-10 w-10 rounded-xl object-contain"
          />
        </picture>
      ) : (
        <span className="text-lg font-semibold text-slate-200">{title.charAt(0)}</span>
      )}
    </div>
  );

  const body = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {brandMark}
          <span className="text-base font-semibold text-slate-200">{title}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span
            className={cn("inline-flex h-2.5 w-2.5 rounded-full", getHealthColor(health))}
            aria-hidden
          />
          <span className="sr-only">{`وضعیت اتصال: ${getHealthLabel(health)}`}</span>
          <span aria-label="زمان آخرین بروزرسانی">
            {isLoading ? "در حال دریافت" : hasTimestamp ? formatRelative(updatedAt) : "داده‌ای دریافت نشد"}
          </span>
          {staleBadge}
        </div>
      </div>
      <div className="mt-6 flex items-end justify-between" aria-live="polite">
        <div aria-busy={isLoading}>
          <div className={cn("text-4xl font-bold tracking-tight text-slate-50", isStaleWithTimestamp && "text-slate-400")}>
            {priceContent}
          </div>
          <div className={cn("mt-1 text-sm text-slate-400", isLoading && "text-slate-500", isStaleWithTimestamp && "text-slate-500")}>{currencyContent}</div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-300">{changeContent}</div>
      </div>
    </>
  );

  if (link) {
    return (
      <a
        data-testid={testId}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClass}
        aria-label={`${title} - مشاهده جزئیات در وب‌سایت صرافی`}
        aria-busy={isLoading}
      >
        {body}
      </a>
    );
  }

  return (
    <div data-testid={testId} className={baseClass} aria-label={`${title} - کارت قیمت`} aria-busy={isLoading}>
      {body}
    </div>
  );
}
